import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import AlertToast from '../../../components/common/AlertToast';
import PolicyTab from '../../../components/common/PolicyTab';

// ─── Constants ──────────────────────────────────────────────────────────────
const EXAM_TYPES = [
  { value: 'unit_test',   label: 'Unit Test' },
  { value: 'midterm',     label: 'Midterm' },
  { value: 'half_yearly', label: 'Half Yearly' },
  { value: 'annual',      label: 'Annual' },
  { value: 'pre_board',   label: 'Pre-Board' },
  { value: 'practice',    label: 'Practice' },
  { value: 'custom',      label: 'Custom' },
];
const DIFFICULTIES = [
  { value: 'easy',   label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard',   label: 'Hard' },
  { value: 'mixed',  label: 'Mixed' },
];
const QUESTION_TYPES = [
  { value: 'mcq',           label: 'MCQ' },
  { value: 'true_false',    label: 'True / False' },
  { value: 'fill_blank',    label: 'Fill the Blank' },
  { value: 'short_answer',  label: 'Short Answer' },
  { value: 'long_answer',   label: 'Long Answer' },
];
const STATUS_BADGE = {
  draft:     { bs: 'secondary', label: 'Draft' },
  generated: { bs: 'info',      label: 'Generated' },
  reviewed:  { bs: 'warning',   label: 'Reviewed' },
  published: { bs: 'success',   label: 'Published' },
};

const blankForm = {
  subject: '', classGrade: '', section: '',
  examType: 'unit_test', difficulty: 'mixed',
  totalMarks: 50, durationMinutes: 60,
  // Chapter source has 3 modes: 'syllabus' (multi-select from fetched data),
  // 'manual' (free-text), 'whole' (whole syllabus). Manual preserves chapter
  // input independently so toggling the mode doesn't lose typed text.
  chapterMode: 'syllabus',
  chapter: '',                    // free-text manual entry
  selectedChapters: [],           // chapter names selected from syllabus
  selectedTopics: [],             // topic titles narrowed under selected chapters
  questionCount: 10,
  questionTypes: ['mcq', 'short_answer'],
  instructions: '', academicYear: '',
};

// Pull the school config so we can drive class / section / subject dropdowns.
async function loadSchoolConfig() {
  try {
    const r = await api.get('/records/school_config', { params: { limit: 1 } });
    const rec = (r.data?.records || [])[0];
    return rec?.recordData || {};
  } catch (_) { return {}; }
}

// ─── Main ───────────────────────────────────────────────────────────────────
export default function QuestionPaperGenerator() {
  const { user } = useAuth();
  const role = user?.roleKey || '';
  const canWrite = ['admin','manager','team_lead','faculty','teacher'].includes(role) || user?.userType === 'super_admin';

  const [tab, setTab]               = useState('generate');     // 'generate' | 'library' | 'policy'
  const [form, setForm]             = useState(blankForm);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated]   = useState(null);            // { title, instructions, questions, rawAiResponse }
  const [saving, setSaving]         = useState(false);

  const [library, setLibrary]       = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [viewing, setViewing]       = useState(null);            // full paper for view modal

  const [config, setConfig]         = useState({ classes: [], sections: [], subjects: [] });
  const [syllabus, setSyllabus]     = useState(null);   // recordData.chapters[] for current ctx
  const [syllabusLoading, setSyllabusLoading] = useState(false);
  const [toast, setToast]           = useState(null);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Load school config once for dropdowns.
  useEffect(() => {
    loadSchoolConfig().then(c => setConfig({
      classes:  Array.isArray(c.classes)  ? c.classes  : [],
      sections: Array.isArray(c.sections) ? c.sections : [],
      subjects: Array.isArray(c.subjects) ? c.subjects : [],
    }));
  }, []);

  // Whenever class/section/subject change, fetch the matching syllabus so
  // the Chapter / Topic dropdowns can populate from real data the school has
  // already entered. Falls back gracefully when no syllabus exists for that combo.
  useEffect(() => {
    const ctx = form;
    if (!ctx.classGrade || !ctx.section || !ctx.subject) {
      setSyllabus(null);
      // Reset selections so stale chapter names don't end up in the AI request.
      setForm(p => ({ ...p, selectedChapters: [], selectedTopics: [] }));
      return;
    }
    setSyllabusLoading(true);
    api.get('/education/syllabus', {
      params: { class_grade: ctx.classGrade, section: ctx.section, subject: ctx.subject },
    })
      .then(r => {
        const rec = r.data?.data || r.data;
        const chs = rec?.recordData?.chapters || [];
        setSyllabus(chs);
        // Reset selections when context changes — they refer to old chapter names.
        setForm(p => ({ ...p, selectedChapters: [], selectedTopics: [] }));
      })
      .catch(() => setSyllabus(null))
      .finally(() => setSyllabusLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.classGrade, form.section, form.subject]);

  // Topics under currently-selected chapters (for the topic narrowing checkboxes).
  const availableTopicsByChapter = (syllabus || [])
    .filter(ch => form.selectedChapters.includes(ch.name))
    .map(ch => ({ chapter: ch.name, topics: (ch.topics || []).map(t => t.title).filter(Boolean) }));

  const toggleArr = (key, value) => setForm(p => {
    const arr = p[key] || [];
    const next = arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value];
    return { ...p, [key]: next };
  });

  const fetchLibrary = useCallback(async () => {
    setLibLoading(true);
    try {
      const r = await api.get('/education/question-papers', { params: { limit: 50 } });
      setLibrary(r.data?.records || []);
    } catch (_) {}
    setLibLoading(false);
  }, []);

  useEffect(() => { if (tab === 'library') fetchLibrary(); }, [tab, fetchLibrary]);

  // ─── Generate ────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!form.subject)    return setToast({ type: 'danger', msg: 'Subject is required.' });
    if (!form.classGrade) return setToast({ type: 'danger', msg: 'Class / Grade is required.' });
    if (!form.questionCount || form.questionCount < 1) return setToast({ type: 'danger', msg: 'Question count must be at least 1.' });

    setGenerating(true);
    setGenerated(null);
    try {
      // Build the chapters[] sent to the AI based on the selected chapter mode.
      // 'syllabus' → selected chapter names + (optionally) selected topic titles
      // 'manual'   → just the free-text chapter field (split on commas)
      // 'whole'    → empty array (AI is told "whole syllabus")
      let chapters = [];
      if (form.chapterMode === 'syllabus') {
        chapters = [
          ...form.selectedChapters,
          ...form.selectedTopics.map(t => `Topic: ${t}`),
        ];
      } else if (form.chapterMode === 'manual') {
        chapters = (form.chapter || '').split(',').map(s => s.trim()).filter(Boolean);
      }
      const payload = { ...form, chapters, chapter: form.chapterMode === 'manual' ? form.chapter : '' };
      const r = await api.post('/education/question-papers/generate', payload);
      const data = r.data?.data || r.data;
      if (!data?.questions?.length) {
        setToast({ type: 'danger', msg: 'AI returned no questions. Try again.' });
      } else {
        setGenerated(data);
        setToast({ type: 'success', msg: `Generated ${data.questions.length} questions.` });
      }
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Generation failed.' });
    }
    setGenerating(false);
  };

  // Edit a generated question inline before saving.
  const updateGeneratedQuestion = (idx, patch) => {
    setGenerated(prev => {
      if (!prev) return prev;
      const next = { ...prev, questions: prev.questions.slice() };
      next.questions[idx] = { ...next.questions[idx], ...patch };
      return next;
    });
  };
  const removeGeneratedQuestion = (idx) => {
    setGenerated(prev => prev && ({ ...prev, questions: prev.questions.filter((_, i) => i !== idx) }));
  };

  // ─── Save to library ─────────────────────────────────────────────────────
  const handleSave = async (status = 'generated') => {
    if (!generated) return;
    setSaving(true);
    try {
      const totalMarks = generated.questions.reduce((s, q) => s + (Number(q.marks) || 0), 0);
      // Mirror the chapters[] resolution from handleGenerate so the saved
      // record carries the same chapter list the AI was asked about.
      let chapters = [];
      if (form.chapterMode === 'syllabus') {
        chapters = [...form.selectedChapters, ...form.selectedTopics.map(t => `Topic: ${t}`)];
      } else if (form.chapterMode === 'manual') {
        chapters = (form.chapter || '').split(',').map(s => s.trim()).filter(Boolean);
      }
      const payload = {
        ...form,
        title: generated.title || `${form.subject} — ${form.classGrade} ${form.examType}`,
        instructions: generated.instructions || form.instructions,
        questions: generated.questions,
        totalMarks,
        chapters,
        chapter: form.chapterMode === 'manual' ? form.chapter : (form.selectedChapters[0] || ''),
        status,
        rawAiResponse: generated.rawAiResponse,
      };
      const r = await api.post('/education/question-papers', payload);
      setToast({ type: 'success', msg: 'Saved to library.' });
      setGenerated(null);
      setTab('library');
      fetchLibrary();
    } catch (e) {
      setToast({ type: 'danger', msg: e.response?.data?.message || 'Save failed.' });
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this paper?')) return;
    try {
      await api.delete(`/education/question-papers/${id}`);
      setToast({ type: 'success', msg: 'Deleted.' });
      fetchLibrary();
    } catch (_) {
      setToast({ type: 'danger', msg: 'Delete failed.' });
    }
  };

  const handleView = async (id) => {
    try {
      const r = await api.get(`/education/question-papers/${id}`);
      setViewing(r.data?.data || r.data);
    } catch (_) {
      setToast({ type: 'danger', msg: 'Failed to load paper.' });
    }
  };

  // Plain-text export — opens a new tab; the user can save / print from there.
  const exportAsText = (paper) => {
    const lines = [];
    lines.push(paper.title || 'Question Paper');
    lines.push('='.repeat((paper.title || 'Question Paper').length));
    lines.push(`Subject: ${paper.subject}    Grade: ${paper.classGrade}${paper.section ? ` ${paper.section}` : ''}`);
    lines.push(`Total Marks: ${paper.totalMarks}    Duration: ${paper.durationMinutes} min`);
    if (paper.instructions) { lines.push(''); lines.push(paper.instructions); }
    lines.push('');
    paper.questions.forEach((q, i) => {
      lines.push(`${q.questionNumber || i + 1}. ${q.text}    [${q.marks} ${q.marks === 1 ? 'mark' : 'marks'}]`);
      if (q.options && q.options.length) q.options.forEach((opt, j) => lines.push(`    ${String.fromCharCode(65 + j)}. ${opt}`));
      lines.push('');
    });
    lines.push('---');
    lines.push('Answer Key');
    paper.questions.forEach((q, i) => {
      if (q.correctAnswer) lines.push(`${q.questionNumber || i + 1}. ${q.correctAnswer}${q.explanation ? ` — ${q.explanation}` : ''}`);
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  return (
    <div>
      <PageHeader title="Question Paper Generator" icon="bi-robot"
        subtitle="AI-powered question paper generation for school exams" />
      {toast && <AlertToast type={toast.type} message={toast.msg} show={true} onClose={() => setToast(null)} />}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item"><button className={`nav-link ${tab === 'generate' ? 'active' : ''}`} onClick={() => setTab('generate')}>
          <i className="bi bi-magic me-1"/>Generate
        </button></li>
        <li className="nav-item"><button className={`nav-link ${tab === 'library' ? 'active' : ''}`} onClick={() => setTab('library')}>
          <i className="bi bi-collection me-1"/>Library
        </button></li>
        <li className="nav-item"><button className={`nav-link ${tab === 'policy' ? 'active' : ''}`} onClick={() => setTab('policy')}>
          <i className="bi bi-journal-text me-1"/>Policy & Manual
        </button></li>
      </ul>

      {/* ────────────────────────────────────────────── GENERATE TAB ───── */}
      {tab === 'generate' && (
        <>
          <div className="card mb-3">
            <div className="card-header fw-semibold"><i className="bi bi-sliders me-2 text-primary"/>Paper Configuration</div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label">Subject <span className="text-danger">*</span></label>
                  {config.subjects.length ? (
                    <select className="form-select" value={form.subject} onChange={e => setF('subject', e.target.value)}>
                      <option value="">Select subject…</option>
                      {config.subjects.map(s => <option key={s} value={s}>{s}</option>)}
                      <option value="__other">Other (type below)</option>
                    </select>
                  ) : null}
                  {(!config.subjects.length || form.subject === '__other') && (
                    <input className="form-control mt-1" placeholder="e.g. Mathematics"
                      value={form.subject === '__other' ? '' : form.subject}
                      onChange={e => setF('subject', e.target.value)} />
                  )}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Class / Grade <span className="text-danger">*</span></label>
                  {config.classes.length ? (
                    <select className="form-select" value={form.classGrade} onChange={e => setF('classGrade', e.target.value)}>
                      <option value="">Select class…</option>
                      {config.classes.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : (
                    <input className="form-control" placeholder="e.g. Class 10" value={form.classGrade} onChange={e => setF('classGrade', e.target.value)} />
                  )}
                </div>
                <div className="col-md-2">
                  <label className="form-label">Section</label>
                  {config.sections.length ? (
                    <select className="form-select" value={form.section} onChange={e => setF('section', e.target.value)}>
                      <option value="">All</option>
                      {config.sections.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <input className="form-control" placeholder="A" value={form.section} onChange={e => setF('section', e.target.value)} />
                  )}
                </div>
                <div className="col-md-3">
                  <label className="form-label">Exam Type</label>
                  <select className="form-select" value={form.examType} onChange={e => setF('examType', e.target.value)}>
                    {EXAM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Difficulty</label>
                  <select className="form-select" value={form.difficulty} onChange={e => setF('difficulty', e.target.value)}>
                    {DIFFICULTIES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Total Marks</label>
                  <input type="number" min="1" className="form-control" value={form.totalMarks} onChange={e => setF('totalMarks', parseInt(e.target.value) || 0)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Duration (min)</label>
                  <input type="number" min="1" className="form-control" value={form.durationMinutes} onChange={e => setF('durationMinutes', parseInt(e.target.value) || 0)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Number of Questions</label>
                  <input type="number" min="1" max="50" className="form-control" value={form.questionCount} onChange={e => setF('questionCount', parseInt(e.target.value) || 0)} />
                </div>

                {/* ─── Chapter / Topic source picker ───────────────────── */}
                <div className="col-12">
                  <label className="form-label d-flex align-items-center gap-2 mb-1">
                    <span>Chapter / Topic Source</span>
                    {syllabusLoading && <span className="spinner-border spinner-border-sm text-primary"/>}
                    {syllabus !== null && !syllabusLoading && (
                      <span className="badge bg-success-subtle text-success small">
                        <i className="bi bi-check-circle me-1"/>{syllabus.length} chapter(s) found in syllabus
                      </span>
                    )}
                    {syllabus === null && form.classGrade && form.section && form.subject && !syllabusLoading && (
                      <span className="badge bg-warning-subtle text-warning small">
                        <i className="bi bi-exclamation-triangle me-1"/>No syllabus configured for this combo — use Manual
                      </span>
                    )}
                  </label>
                  <div className="d-flex gap-2 mb-2 flex-wrap">
                    {[
                      { v: 'syllabus', label: 'From Syllabus', icon: 'bi-journal-bookmark', disabled: !syllabus || syllabus.length === 0 },
                      { v: 'manual',   label: 'Manual (type your own)', icon: 'bi-pencil-square' },
                      { v: 'whole',    label: 'Whole Syllabus', icon: 'bi-book' },
                    ].map(o => (
                      <button key={o.v} type="button"
                        className={`btn btn-sm ${form.chapterMode === o.v ? 'btn-primary' : 'btn-outline-primary'}`}
                        disabled={o.disabled}
                        onClick={() => setF('chapterMode', o.v)}>
                        <i className={`bi ${o.icon} me-1`}/>{o.label}
                      </button>
                    ))}
                  </div>

                  {/* Mode-specific UI */}
                  {form.chapterMode === 'syllabus' && syllabus && syllabus.length > 0 && (
                    <div className="border rounded p-2" style={{ background: '#f8fafc', maxHeight: 280, overflowY: 'auto' }}>
                      <div className="small text-muted mb-2">
                        Select one or more chapters; optionally narrow to specific topics.
                      </div>
                      {syllabus.map(ch => {
                        const checked = form.selectedChapters.includes(ch.name);
                        const topics  = (ch.topics || []).map(t => t.title).filter(Boolean);
                        return (
                          <div key={ch._id || ch.name} className="mb-2">
                            <label className="d-flex align-items-center gap-2">
                              <input type="checkbox" className="form-check-input"
                                checked={checked}
                                onChange={() => toggleArr('selectedChapters', ch.name)} />
                              <span className="fw-semibold">{ch.name}</span>
                              <span className="badge bg-light text-dark border small">{topics.length} topic(s)</span>
                            </label>
                            {checked && topics.length > 0 && (
                              <div className="ms-4 mt-1 d-flex flex-wrap gap-2">
                                {topics.map((t, ti) => {
                                  const tChecked = form.selectedTopics.includes(t);
                                  return (
                                    <label key={`${ch._id}_${ti}`} className="d-flex align-items-center gap-1 small px-2 py-1 rounded"
                                      style={{ background: tChecked ? '#dbeafe' : '#fff', border: '1px solid #e2e8f0', cursor: 'pointer' }}>
                                      <input type="checkbox" className="form-check-input"
                                        checked={tChecked}
                                        onChange={() => toggleArr('selectedTopics', t)} />
                                      {t}
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {form.selectedChapters.length === 0 && (
                        <div className="small text-muted text-center py-2">
                          <i className="bi bi-arrow-up me-1"/>Tick chapters to include in the paper.
                        </div>
                      )}
                    </div>
                  )}

                  {form.chapterMode === 'manual' && (
                    <input className="form-control"
                      placeholder="Type chapter / topic names, comma-separated. e.g. Chapter 3: Quadratic Equations, Chapter 4: Geometry"
                      value={form.chapter}
                      onChange={e => setF('chapter', e.target.value)} />
                  )}

                  {form.chapterMode === 'whole' && (
                    <div className="alert alert-info py-2 small mb-0">
                      <i className="bi bi-info-circle me-1"/>The AI will be told to draw from the entire syllabus.
                    </div>
                  )}
                </div>

                <div className="col-12">
                  <label className="form-label">Question Types Allowed</label>
                  <div className="d-flex flex-wrap gap-2">
                    {QUESTION_TYPES.map(t => (
                      <label key={t.value} className="d-flex align-items-center gap-1 small">
                        <input type="checkbox"
                          checked={form.questionTypes.includes(t.value)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...form.questionTypes, t.value]
                              : form.questionTypes.filter(x => x !== t.value);
                            setF('questionTypes', next);
                          }} />
                        {t.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="col-md-9">
                  <label className="form-label">Special Instructions for the Paper</label>
                  <textarea className="form-control" rows={2} placeholder="Any specific guidance for the AI — e.g. 'Include 2 word problems' or 'Avoid topics not yet taught'."
                    value={form.instructions} onChange={e => setF('instructions', e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Academic Year</label>
                  <input className="form-control" placeholder="e.g. 2026-27" value={form.academicYear} onChange={e => setF('academicYear', e.target.value)} />
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <button className="btn btn-outline-secondary" onClick={() => { setForm(blankForm); setGenerated(null); }} disabled={generating}>
                  <i className="bi bi-arrow-counterclockwise me-1"/>Reset
                </button>
                <button className="btn btn-primary" onClick={handleGenerate} disabled={generating || !canWrite}>
                  {generating
                    ? <><span className="spinner-border spinner-border-sm me-2"/>Generating with AI…</>
                    : <><i className="bi bi-magic me-1"/>Generate Questions</>}
                </button>
              </div>
              {!canWrite && <small className="text-muted d-block mt-2">Your role does not have permission to generate papers.</small>}
            </div>
          </div>

          {/* ─── Generated paper preview ─────────────────────────────── */}
          {generating && (
            <div className="card">
              <div className="card-body text-center py-5">
                <LoadingSpinner />
                <p className="text-muted mt-3 mb-0">Asking the AI to draft the paper… this typically takes 20–60 seconds.</p>
              </div>
            </div>
          )}

          {!generating && generated && (
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <span className="fw-semibold"><i className="bi bi-file-earmark-text me-2 text-success"/>{generated.title}</span>
                  <span className="badge bg-info ms-2">{generated.questions.length} questions</span>
                  <span className="badge bg-secondary ms-1">{generated.questions.reduce((s, q) => s + (q.marks || 0), 0)} marks</span>
                </div>
                <div className="d-flex gap-2">
                  <button className="btn btn-outline-primary btn-sm" onClick={() => exportAsText({ ...form, ...generated, totalMarks: generated.questions.reduce((s, q) => s + (q.marks || 0), 0) })}>
                    <i className="bi bi-download me-1"/>Export Text
                  </button>
                  <button className="btn btn-outline-secondary btn-sm" onClick={handleGenerate} disabled={generating}>
                    <i className="bi bi-arrow-clockwise me-1"/>Regenerate
                  </button>
                  <button className="btn btn-success btn-sm" onClick={() => handleSave('generated')} disabled={saving}>
                    {saving ? <span className="spinner-border spinner-border-sm me-1"/> : <i className="bi bi-save me-1"/>}Save to Library
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={() => handleSave('published')} disabled={saving}>
                    <i className="bi bi-check-circle me-1"/>Save & Publish
                  </button>
                </div>
              </div>
              <div className="card-body">
                {generated.instructions && (
                  <div className="alert alert-info py-2 small">
                    <strong>Instructions:</strong> {generated.instructions}
                  </div>
                )}
                {generated.questions.map((q, i) => (
                  <div key={i} className="border rounded p-3 mb-2" style={{ background: '#fafbfc' }}>
                    <div className="d-flex justify-content-between gap-2 mb-2">
                      <div className="flex-grow-1">
                        <span className="fw-semibold me-2">Q{q.questionNumber || i + 1}.</span>
                        <span className="badge bg-light text-dark border me-2">{q.type.replace('_', ' ')}</span>
                        <span className="badge bg-secondary">{q.marks} {q.marks === 1 ? 'mark' : 'marks'}</span>
                      </div>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => removeGeneratedQuestion(i)} title="Remove question">
                        <i className="bi bi-x"/>
                      </button>
                    </div>
                    <textarea className="form-control form-control-sm mb-2" rows={2}
                      value={q.text} onChange={e => updateGeneratedQuestion(i, { text: e.target.value })} />
                    {q.options && q.options.length > 0 && (
                      <div className="ms-4 small">
                        {q.options.map((opt, j) => (
                          <div key={j} className="d-flex gap-2 align-items-center mb-1">
                            <span className="text-muted">{String.fromCharCode(65 + j)}.</span>
                            <input className="form-control form-control-sm" value={opt}
                              onChange={e => {
                                const next = q.options.slice();
                                next[j] = e.target.value;
                                updateGeneratedQuestion(i, { options: next });
                              }} />
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="row g-2 mt-1">
                      <div className="col-md-6">
                        <input className="form-control form-control-sm" placeholder="Correct answer / answer key"
                          value={q.correctAnswer || ''} onChange={e => updateGeneratedQuestion(i, { correctAnswer: e.target.value })} />
                      </div>
                      <div className="col-md-6">
                        <input className="form-control form-control-sm" placeholder="Explanation (optional)"
                          value={q.explanation || ''} onChange={e => updateGeneratedQuestion(i, { explanation: e.target.value })} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ────────────────────────────────────────────── LIBRARY TAB ────── */}
      {tab === 'library' && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span className="fw-semibold"><i className="bi bi-collection me-2 text-primary"/>Saved Question Papers</span>
            <button className="btn btn-outline-secondary btn-sm" onClick={fetchLibrary}><i className="bi bi-arrow-clockwise"/></button>
          </div>
          <div className="card-body p-0">
            {libLoading ? (
              <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"/></div>
            ) : library.length === 0 ? (
              <div className="text-center text-muted py-5"><i className="bi bi-inbox fs-1 d-block mb-2"/>No saved papers yet. Generate one in the Generate tab.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Title</th>
                      <th>Subject</th>
                      <th>Grade</th>
                      <th>Type</th>
                      <th>Marks</th>
                      <th>Questions</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {library.map(p => {
                      const sb = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
                      return (
                        <tr key={p._id}>
                          <td className="fw-semibold">{p.title}</td>
                          <td>{p.subject}</td>
                          <td>{p.classGrade}{p.section ? ` ${p.section}` : ''}</td>
                          <td><span className="badge bg-light text-dark border">{(p.examType || '').replace('_', ' ')}</span></td>
                          <td>{p.totalMarks}</td>
                          <td>{p.questions?.length || 0}</td>
                          <td><span className={`badge bg-${sb.bs}`}>{sb.label}</span></td>
                          <td className="small text-muted">{new Date(p.createdAt).toLocaleDateString('en-IN')}</td>
                          <td className="text-end">
                            <div className="d-flex gap-1 justify-content-end">
                              <button className="btn btn-outline-secondary btn-sm" onClick={() => handleView(p._id)} title="View"><i className="bi bi-eye"/></button>
                              <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(p._id)} title="Delete"><i className="bi bi-trash"/></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────── POLICY TAB ─────── */}
      {tab === 'policy' && <PolicyTab moduleKey="question_paper_generator" label="Question Paper Generator — Manual & Policy" />}

      {/* ─── View modal ──────────────────────────────────────────────── */}
      {viewing && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{viewing.title}</h5>
                <button className="btn-close" onClick={() => setViewing(null)}/>
              </div>
              <div className="modal-body">
                <div className="d-flex flex-wrap gap-3 small mb-3 pb-2 border-bottom">
                  <span><strong>Subject:</strong> {viewing.subject}</span>
                  <span><strong>Class:</strong> {viewing.classGrade}{viewing.section ? ` ${viewing.section}` : ''}</span>
                  <span><strong>Type:</strong> {(viewing.examType || '').replace('_', ' ')}</span>
                  <span><strong>Marks:</strong> {viewing.totalMarks}</span>
                  <span><strong>Duration:</strong> {viewing.durationMinutes} min</span>
                  <span><strong>Difficulty:</strong> {viewing.difficulty}</span>
                </div>
                {viewing.instructions && (
                  <div className="alert alert-info py-2 small"><strong>Instructions:</strong> {viewing.instructions}</div>
                )}
                {(viewing.questions || []).map((q, i) => (
                  <div key={i} className="border rounded p-2 mb-2">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="fw-semibold">Q{q.questionNumber || i + 1}. {q.text}</span>
                      <span className="badge bg-secondary">{q.marks} {q.marks === 1 ? 'mark' : 'marks'}</span>
                    </div>
                    {q.options && q.options.length > 0 && (
                      <ol type="A" className="small mb-1">{q.options.map((opt, j) => <li key={j}>{opt}</li>)}</ol>
                    )}
                    {q.correctAnswer && <div className="small text-success"><i className="bi bi-check-circle me-1"/><strong>Answer:</strong> {q.correctAnswer}</div>}
                    {q.explanation && <div className="small text-muted">{q.explanation}</div>}
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-primary btn-sm" onClick={() => exportAsText(viewing)}>
                  <i className="bi bi-download me-1"/>Export Text
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setViewing(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
